import jstz from 'jstz';
import { ImpactTypes } from '../types';
import { ComputeDimensionsRequest, SearchServicePublic } from '@fullstory/proto/fs/services/alexa';
import { ConversionsPublic } from '@fullstory/proto/fs/services/webber/internal/conversions';
import { ActionType, createAction } from '@fullstory/redux-util/utils';
import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import { hashObject } from '@fullstory/util/hash';
import { getSearchCacheKey, SearchCacheKey } from '../../searchMeta';
import { getDimensionsRequest, getSnapshotDimensionsRequest } from '../selectors';
import { AppDispatch, AppState, AppThunkAction } from '../../../types';
import { Flag } from '@fullstory/proto/fs/shared/flags';

export const ImpactDimensionsActions = {
  computeDimensionsRequest: () =>
    createAction({
      type: ImpactTypes.ComputeDimensionsRequest,
    }),
};

export const ImpactDimensionsRequestActions = {
  computeDimensionsRequest: (
    req: Omit<ComputeDimensionsRequest, 'now' | 'timezone'>,
    searchCacheKey: SearchCacheKey,
  ) => {
    const body = {
      now: null, // current time is implied
      timezone: jstz.determine().name(),
      ...req,
    };
    return createGRPCAction<typeof SearchServicePublic.ComputeDimensions>({
      cacheKey: (url: string): string => {
        const { cacheOptions, ...requestCacheData } = body;
        return `${url}:${hashObject(requestCacheData)}:${hashObject(searchCacheKey)}`;
      },
    })(
      {
        type: ImpactTypes.ComputeDimensionsRequest,
        grpcFunction: SearchServicePublic.ComputeDimensions,
        body,
        readWrite: 'write',
      },
      ImpactTypes.ComputeDimensionsSuccess,
      ImpactTypes.ComputeDimensionsFailure,
    );
  },
  computeDimensionsForSnapshot: (
    req: Omit<ComputeDimensionsRequest, 'now' | 'timezone'>,
    searchCacheKey: SearchCacheKey,
  ) => {
    const body = {
      now: null, // current time is implied
      timezone: jstz.determine().name(),
      ...req,
    };
    return createGRPCAction<typeof ConversionsPublic.ComputeDimensionsForGuestSnapshot>({
      cacheKey: (url: string): string => {
        const { cacheOptions, ...requestCacheData } = body;
        return `${url}:${hashObject(requestCacheData)}:${hashObject(searchCacheKey)}`;
      },
    })(
      {
        type: ImpactTypes.DeleteOpportunitySnapshotRequest,
        grpcFunction: ConversionsPublic.ComputeDimensionsForGuestSnapshot,
        body,
        readWrite: 'write',
      },
      ImpactTypes.DeleteOpportunitySnapshotSuccess,
      ImpactTypes.DeleteOpportunitySnapshotFailure,
    );
  },
};

export type ImpactDimensionsActions =
  | ActionType<typeof ImpactDimensionsActions>
  | RequestActionType<typeof ImpactDimensionsRequestActions>;

export type DimensionThunkAction<R> = AppThunkAction<R, ImpactDimensionsActions>;
export const ImpactDimensionsAsyncActions = {
  computeDimensions:
    (): DimensionThunkAction<void> => async (dispatch: AppDispatch, getState: () => AppState) => {
      const req = getDimensionsRequest(getState());
      if (!req) {
        throw new Error('Compute Dimensions request is undefined');
      }
      const searchCacheKey = getSearchCacheKey(getState());
      dispatch(ImpactDimensionsRequestActions.computeDimensionsRequest(req, searchCacheKey));
    },
  computeSnapshotDimensions:
    (): DimensionThunkAction<void> =>
    async (dispatch: AppDispatch, getState: () => AppState, { flags }) => {
      const req = getSnapshotDimensionsRequest(getState());
      if (!req) {
        throw new Error('Compute Dimensions request is undefined');
      }
      const searchCacheKey = getSearchCacheKey(getState());
      if (flags().hasBit(Flag.FeatImpacts)) {
        return dispatch(
          ImpactDimensionsRequestActions.computeDimensionsRequest(req, searchCacheKey),
        );
      } else {
        return dispatch(
          ImpactDimensionsRequestActions.computeDimensionsForSnapshot(req, searchCacheKey),
        );
      }
    },
};
